import React, { useMemo, useState, useEffect,useContext } from "react";
import { getLocale, shallowEqual, useIntl, useSelector } from "umi";
import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";
import SelectShowValueType, { ShowValueType } from "@/pages/dashboard/pages/overview/components/OverviewTable/components/SelectShowValueType";
import MetricsSettings, { defaultStatisticsTypeValue } from "@/pages/dashboard/components/MetricsSettings";
import {
  useStatisticsDataFormat,
  useTableColumns,
  useTableDataFormat
} from "@/pages/dashboard/pages/overview/components/OverviewTable/hooks";
import useTableLoclesIntl from "@/pages/dashboard/pages/overview/_config/useTableLoclesIntl";
import { LANGUAGE_MAP } from "@/pages/dashboard/_config/constant";
import DimensionAccumulateRow from "@/pages/dashboard/components/DimensionAccumulateRow";
import useData from "./useData";
import { TableProps } from "./types";
import { useSelectArrObj } from "@/pages/dashboard/utils/customHook";
import { FilterContextType } from "@/pages/dashboard/components/PageContainer/type";
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";

import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";


export default function Table(props: TableProps) {
  // const {filterVal,filterLoading,} = props;
  const contextObj: FilterContextType = useContext(FilterContext)
  const { filterVal, filterLoading } = contextObj || {};
  const explainInfo = useSelector((state: any) => state.dashboard?.explainInfo, shallowEqual);
  const { formatMessage } = useIntl();
  const [columns, setColumns] = useState([]);
  //列定义, 中英文切换(全部), Accumulate关闭(仅仅维度列),
  const { COLUMN_CONFIG_ALL } = useTableLoclesIntl()
  const COLUMN_CONFIG = COLUMN_CONFIG_ALL['pcConsoleDayV2'] || []
  const [showMetrics, setShowMetrics] = useState(Object.keys(COLUMN_CONFIG));
  const [selectType, setSelectType] = useState<ShowValueType>("value_only");
  const [showStatistic, setShowStatistic] = useState(defaultStatisticsTypeValue)
  const [accumulated, setAccumulated] = useState<string[]>(['date']);
  let SEPARATED_OPTIONS = useSelectArrObj(filterVal?.filterValue)
  SEPARATED_OPTIONS = SEPARATED_OPTIONS?.filter(i => i?.groupKey === 'separated')
  SEPARATED_OPTIONS.unshift({
    groupKey: 'separated',
    value: 'date',
    text: formatMessage({ id: 'common_by_Date' }),
    disabled: true,
  })

  const { DATE_COLUMN, getAccumulateColumns, getMetricColumns } = useTableColumns()

  const compData = useData(filterVal, filterLoading, selectType)

  const {
    loading, isError, paging, setPaging, sort, setSort,
    total, tableData, statisticsData, filterValueSort, accumulateOldValueRef,
    getRequestParams, getData, downLoadData
  } = compData || {}

  useEffect(() => {
    const columns = [
      DATE_COLUMN,
      ...getAccumulateColumns(accumulateOldValueRef?.current),
      ...getMetricColumns(showMetrics, COLUMN_CONFIG, explainInfo, selectType)
    ]
    setColumns(columns);
  }, [accumulateOldValueRef?.current, selectType, filterVal.timeType, showMetrics, getLocale()])


  // 表格数据
  const records = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterVal.timeType),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic)
  ]

  const copyData = [
    ...useTableDataFormat(tableData, COLUMN_CONFIG, filterVal.timeType, true),
    ...useStatisticsDataFormat(statisticsData, COLUMN_CONFIG, showStatistic, true)
  ]

  // 下载弹窗指标options
  const metricOptions = useMemo(() => {
    return Object.keys(COLUMN_CONFIG).map((key) => ({ value: key, text: COLUMN_CONFIG[key]?.name }))
  }, [COLUMN_CONFIG_ALL])

  const onSortChange = (sorts) => {
    // 排序
    setSort(sorts)
    // 发起请求
    const params = getRequestParams(filterValueSort?.current?.paging, sorts, filterValueSort?.current?.filterValue)
    // forceUpdate()
    getData(params);
  }

  return (
    <PerformanceMetricsTable
      title={useIntl().formatMessage({ id: "overview_Overall_Performance" })}
      // className='overall-conext'
      columns={columns}
      records={records}
      copyConfig={
        {
          copyData: copyData
        }
      }
      selectType={selectType}
      timeType={filterVal?.timeType}
      sorting={{
        defaultSorts: sort,
        onSortChange: onSortChange,

      }}
      paging={{
        pageIndex: paging.pageIndex,
        pageSize: paging.pageSize,
        recordCount: total,
        pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
        onPagingChange(query) {
          // 切换分页
          setPaging(query);
          // 发起请求
          const params = getRequestParams(query, sort, filterValueSort?.current?.filterValue)
          getData(params);
        }
      }}
      topTip={{
        left: (<div className={`overall-conext-margin js-overview-table-dimension`}>
          <DimensionAccumulateRow
            options={SEPARATED_OPTIONS}
            value={accumulated}
            onChange={(value) => {
              setAccumulated(value);
              if (accumulateOldValueRef?.current?.toString() !== value?.toString()) {
                accumulateOldValueRef.current = value;
                // 重置分页和排序 (不把重置过程写到 getRequestParams中, 是想要保证方法的独立性, 不要有多种作用混杂在一起)
                setPaging({ pageIndex: 1, pageSize: paging.pageSize });
                setSort([])
                // // 发起请求
                const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, [], filterValueSort?.current?.filterValue)
                getData(params);
              }
            }}
          />
          <SelectShowValueType
            timeType={filterVal.timeType}
            value={selectType}
            onChange={(value) => { setSelectType(value) }}
            isShowDod
            isShowSdlw
          />
        </div>),
        right: (<MetricsSettings
          onConfirm={({ metricValues, statisticsType }) => {
            setShowStatistic(statisticsType);
            setShowMetrics(metricValues)
          }}
          disabledOptions={[{ value: "date", text: useIntl().formatMessage({ id: "common_date" }) }]}
          metricOptions={metricOptions}
        />)
      }}
      onResizeEnd={(columns) => setColumns(columns)}
      isShowProgressBar
      loading={loading}
      isError={isError}
      downloadConfig={{
        defaultValue: showMetrics,
        onDownload: ({ isDownloadStatistics, metrics, dataType, fileFormat }) => {
          const params = getRequestParams(paging, sort, filterValueSort?.current?.filterValue, {
            metrics: metrics,
            show_dod: dataType === "show_dod",
            show_sdlw: dataType === "show_sdlw",
            file_format: fileFormat,
            aggregates: isDownloadStatistics ? showStatistic : []
          })
          return downLoadData(params, LANGUAGE_MAP[getLocale()])
        },
        timeType: filterVal.timeType,
        metricOptions: metricOptions,
        showData: { valueOnly: true, showDod: true, showSdlw: true }
      }}
    />)
}
